import { Sprite, Vector2 } from '../../../odie';
import { gsap } from 'gsap';

import { arrayRandom } from '../../../lib/util/jsTools';
import { config } from '../../defs/config';
import {
  spawnBlockEntity,
  BubblesBlockEntity,
  CageBlockEntity,
  despawnBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import {
  mapGetPan,
  mapIterateCells,
  mapToViewPosition,
} from '../../util/mapTools';
import { IEffect } from './IEffect';
import { app } from '../../app';
import { blockPropsMap } from '../../defs/block';
import { canSpawnBubbleOnCell } from '../../util/bubbleTools';

// types
//-----------------------------------------------------------------------------
// public
export type BubblesEffectOptions = {
  subject: BubblesBlockEntity;
};

/*
    spawns one new cage from the position of the bubbles at a random (non bubbles) block
*/
export class BubblesEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _options: BubblesEffectOptions;

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: BubblesEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  public static assets(): string[] {
    return [];
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const promises: Promise<void>[] = [];
    const delay = 0.06;
    const view = this._options.subject.c.blockBubbles.view;
    const bubbles = view.bubbles;

    // Set animating out flag to override bubbles animation loop
    view.isAnimatingOut = true;

    for (let index = 0; index < bubbles.length; index++) {
      const target = this._selectTarget();

      // If there is no target, pop the bubble
      if (!target) {
        promises.push(this._popBubble(bubbles[index].sprite, index * delay));
        continue;
      }

      // Add spawned bubbles (cage) to the goal count
      this.triggerUngoal();

      promises.push(
        this._throwBubble(bubbles[index].sprite, target, index * delay),
      );
    }

    // wait for all the bubbles to be thrown
    await Promise.all(promises);

    // despawn bubbles entity
    despawnBlockEntity(this._scene, this._options.subject);

    return true;
  }

  private triggerUngoal() {
    const goalId = 'cage';

    // Retrieve the current goal count
    const count = this._scene.getGoalCount(goalId);

    // Bubble clusters will create the bubble (cage) goal out of nothing, if not set yet
    this._scene.setGoalCount(goalId, count + 1);

    // Publish the ungoal event, to nitify that the goal has been changed
    this._scene.events.publish({ id: 'ungoal', goalId });
  }

  // private: animations
  //-------------------------------------------------------------------------
  private async _animateThrow(bubble: Sprite, to: Vector2, delay: number) {
    const duration = 0.5 + delay;
    const bubbles = this._options.subject;
    const bubblesPosition = mapToViewPosition(bubbles.c.position.mapPosition);
    const offset = config.tile.size / 2;
    const toPosition = { x: to.x + offset, y: to.y + offset };
    const fromPosition = {
      x: bubblesPosition.x + offset + bubble.x,
      y: bubblesPosition.y + offset + bubble.y,
    };

    bubble.x = fromPosition.x;
    bubble.y = fromPosition.y;
    this._scene.stage.addChild(bubble);

    // Animate the bubble spawning from the bubbles
    gsap.to(bubble, {
      scaleX: 1,
      scaleY: 1,
      duration: duration * 0.5,
      ease: 'power1.out',
    });

    // trajectory animation
    await gsap
      .timeline()
      .to(bubble, {
        y: toPosition.y,
        duration,
        ease: 'quad.out',
      })
      .to(bubble, { x: toPosition.x, duration, ease: 'quad.inOut' }, 0)
      .to(
        bubble,
        {
          scaleX: 1.8,
          scaleY: 1.8,
          duration: duration * 0.5,
          ease: 'quad.inOut',
        },
        0,
      )
      .to(
        bubble,
        {
          scaleX: 1.2,
          scaleY: 1.2,
          duration: duration * 0.5,
          ease: 'quad.inOut',
        },
        duration * 0.5,
      );

    // Remove the individual bubble
    this._scene.stage.removeChild(bubble);
  }

  // private: support
  //-------------------------------------------------------------------------
  private async _throwBubble(bubble: Sprite, target: Vector2, delay = 0) {
    // spawn cage at target position
    const cage = spawnBlockEntity(
      this._scene,
      { id: 'cage' },
      target,
    ) as CageBlockEntity;
    const view = cage.c.view2d.view;

    // Set the cage to be invisible while animating
    view.visible = false;

    // Get the target view position
    const to = mapToViewPosition(target);

    // animate bubble throw
    await this._animateThrow(bubble, to, delay);

    // Set the cage to be visible again
    view.visible = true;

    // play sfx
    app.sound.play('puzzle-bubble-form', { pan: mapGetPan(target), dupes: 10 });

    // animate morphing
    const innerView = cage.c.blockCage.view;
    innerView.scaleX = innerView.scaleY = 1.5;

    await gsap.to(innerView, {
      duration: 0.4,
      scaleX: 1,
      scaleY: 1,
      ease: 'back.out',
    });
  }

  private async _popBubble(bubble: Sprite, delay = 0) {
    // animate pop
    await gsap.to(bubble, {
      duration: 0.4,
      alpha: 0,
      scaleX: 1,
      scaleY: 1,
      ease: 'quad.out',
      delay,
      onStart: () => {
        // play sfx
        app.sound.play('puzzle-bubble-form', {
          pan: mapGetPan({ x: bubble.x, y: bubble.y }),
          dupes: 10,
        });
      },
    });
  }

  /**
   * Select a breakable 1x1 block for a cage bubble.
   *
   * @returns A valid target position, or undefined when none are available.
   */
  private _selectTarget(): Vector2 | undefined {
    const candidates: Vector2[] = [];

    // get target candidate positions
    mapIterateCells(this._scene, (cell, position) => {
      // Cell must support a bubble overlay.
      if (!canSpawnBubbleOnCell(cell)) return;

      // Should have a base.
      if (!cell.base) return;

      // Should be breakable.
      const { blockId } = cell.base.entity.c.block;
      if (blockPropsMap[blockId].immortal) return;

      candidates.push(position);
    });

    // pick a random one
    return arrayRandom(candidates);
  }
}
